export const ONBOARDING_STORAGE_PREFIX = "bellhopt:onboarding";

export const ONBOARDING_STEP_IDS = {
  MODE: "mode",
  STRIPE: "stripe",
  PMS: "pms",
  PMS_PROVIDER: "pms-provider",
  PROPERTY_BASICS: "property-basics",
  PROPERTY_ACCESS: "property-access",
  NOTIFICATIONS: "notifications",
  MESSAGING: "messaging",
  ACTIVATION: "activation",
  SUCCESS: "success",
};

export const ONBOARDING_STEPS = [
  {
    id: ONBOARDING_STEP_IDS.MODE,
    label: "Setup style",
    shortLabel: "Style",
    required: true,
  },
  {
    id: ONBOARDING_STEP_IDS.STRIPE,
    label: "Payouts",
    shortLabel: "Payouts",
    required: false,
    statusType: "payout",
  },
  {
    id: ONBOARDING_STEP_IDS.PMS,
    label: "Property source",
    shortLabel: "Source",
    required: true,
  },
  {
    id: ONBOARDING_STEP_IDS.PMS_PROVIDER,
    label: "PMS connection",
    shortLabel: "PMS",
    required: true,
  },
  {
    id: ONBOARDING_STEP_IDS.PROPERTY_BASICS,
    label: "Property basics",
    shortLabel: "Basics",
    required: true,
  },
  {
    id: ONBOARDING_STEP_IDS.PROPERTY_ACCESS,
    label: "Entry access",
    shortLabel: "Access",
    required: true,
  },
  {
    id: ONBOARDING_STEP_IDS.NOTIFICATIONS,
    label: "Delivery alerts",
    shortLabel: "Alerts",
    required: true,
  },
  {
    id: ONBOARDING_STEP_IDS.MESSAGING,
    label: "Guest messaging",
    shortLabel: "Messaging",
    required: false,
  },
  {
    id: ONBOARDING_STEP_IDS.ACTIVATION,
    label: "Activation",
    shortLabel: "Activate",
    required: true,
  },
];

export const SUPPORTED_PMS_PROVIDERS = ["guesty", "streamline", "hospitable", "track"];
const NO_PMS_VALUES = ["none", "no", "manual", "not_applicable"];

export function getOnboardingStorageKey(hostId) {
  return `${ONBOARDING_STORAGE_PREFIX}:${hostId || "unknown"}`;
}

export function readOnboardingProgress(hostId) {
  if (typeof window === "undefined" || !hostId) return {};
  try {
    const raw = window.localStorage.getItem(getOnboardingStorageKey(hostId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function writeOnboardingProgress(hostId, patch) {
  if (typeof window === "undefined" || !hostId) return {};
  const current = readOnboardingProgress(hostId);
  const next = {
    ...current,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(getOnboardingStorageKey(hostId), JSON.stringify(next));
  return next;
}

export function normalizePmsSelections(raw) {
  const list = Array.isArray(raw) ? raw : typeof raw === "string" ? [raw] : [];
  return list
    .map((item) => String(item || "").trim().toLowerCase())
    .filter(Boolean);
}

export function getPmsDisplayName(provider) {
  const normalized = String(provider || "").toLowerCase();
  const names = {
    guesty: "Guesty",
    streamline: "Streamline",
    hospitable: "Hospitable",
    track: "Track",
    other: "Other PMS",
    none: "No PMS",
    manual: "Manual setup",
  };
  return names[normalized] || (provider ? String(provider) : "PMS");
}

function hasMeaningfulText(value) {
  return String(value || "").trim().length >= 10;
}

function hasValue(value) {
  return String(value || "").trim().length > 0;
}

function getPrimaryPms({ hostInfo, localProgress }) {
  const localProvider = String(localProgress?.pmsProvider || "").trim().toLowerCase();
  if (localProvider) return localProvider;

  const selections = normalizePmsSelections(hostInfo?.propertyManagementSoftware);
  return selections[0] || "";
}

function getPmsDecision({ hostInfo, localProgress, selectedPms }) {
  const localDecision = String(localProgress?.pmsDecision || "").trim().toLowerCase();
  if (localDecision === "yes" || localDecision === "no") return localDecision;
  if (selectedPms) return NO_PMS_VALUES.includes(selectedPms) ? "no" : "yes";
  return "";
}

export function isPmsConnected(hostInfo, provider) {
  const normalized = String(provider || "").trim().toLowerCase();
  if (!normalized || NO_PMS_VALUES.includes(normalized) || normalized === "other") return true;
  if (normalized === "guesty") {
    return Boolean(hostInfo?.guesty_client_id && hostInfo?.guesty_client_secret);
  }
  if (normalized === "streamline") {
    return Boolean(
      (hostInfo?.streamline_api_key && hostInfo?.streamline_api_secret) ||
        (hostInfo?.streamline_token && hostInfo?.streamline_token_secret)
    );
  }
  if (normalized === "hospitable") {
    return Boolean(hostInfo?.hospitableConnectedAt || hostInfo?.hospitableAccountId);
  }
  if (normalized === "track") {
    return Boolean(hostInfo?.trackConnectedAt || hostInfo?.trackBaseUrl);
  }
  return true;
}

export function getConnectedPmsProviders(hostInfo) {
  return SUPPORTED_PMS_PROVIDERS.filter((provider) => isPmsConnected(hostInfo, provider));
}

export function isPropertyBasicsComplete(property) {
  if (!property) return false;
  return Boolean(
    hasValue(property.listingTitle) &&
      hasValue(property.address?.line1) &&
      hasValue(property.address?.city) &&
      hasValue(property.address?.state) &&
      hasValue(property.address?.zipCode) &&
      property.checkInTime &&
      property.checkOutTime
  );
}

export function isPropertyAccessComplete(property) {
  return Boolean(property && hasMeaningfulText(property.description));
}

export function deriveOnboardingState({
  hostInfo,
  hostDetails,
  properties,
  hostFlags,
  localProgress,
} = {}) {
  const progress = localProgress || {};
  const activeProperties = Array.isArray(properties)
    ? properties.filter((property) => !property?.isDeleted)
    : [];
  const firstActiveProperty = activeProperties[0] || null;
  const selectedPms = getPrimaryPms({ hostInfo, localProgress: progress });
  const pmsDecision = getPmsDecision({ hostInfo, localProgress: progress, selectedPms });
  const usesPms = pmsDecision === "yes" && !NO_PMS_VALUES.includes(selectedPms);
  const supportedPms = SUPPORTED_PMS_PROVIDERS.includes(selectedPms);
  const requiresPmsConnection = usesPms && supportedPms;

  const stripeLinked = Boolean(hostDetails?.isOnBoarded ?? hostInfo?.isOnBoarded);
  const stripeSkipped = Boolean(progress.stripeSkippedAt || progress.stripeSkipped);
  const stripeActioned = Boolean(stripeLinked || stripeSkipped);
  const modeChosen = Boolean(progress.setupMode);
  const pmsDecisionMade = Boolean(pmsDecision);
  const pmsProviderSelected = Boolean(!usesPms || selectedPms);
  const pmsConnected = !requiresPmsConnection || isPmsConnected(hostInfo, selectedPms);
  const firstPropertyCreated = activeProperties.length > 0;
  const propertyBasicsConfigured = isPropertyBasicsComplete(firstActiveProperty);
  const entryAccessConfigured = isPropertyAccessComplete(firstActiveProperty);
  const notificationPreferenceConfigured = Boolean(
    hostFlags?.receiveEntryInstructionsEmails || progress.notificationPreferenceConfigured
  );
  const propertyOperationsConfigured = Boolean(
    firstPropertyCreated &&
      propertyBasicsConfigured &&
      entryAccessConfigured &&
      notificationPreferenceConfigured
  );
  const propertySkipped = Boolean(progress.propertySkippedAt);
  const messagingViewed = Boolean(progress.messagingViewed || progress.messagingSkippedAt);
  const propertyReady = propertySkipped || (firstPropertyCreated && propertyBasicsConfigured && entryAccessConfigured);
  const requiredReady = Boolean(
    modeChosen &&
      pmsDecisionMade &&
      pmsProviderSelected &&
      pmsConnected &&
      propertyReady &&
      (notificationPreferenceConfigured || propertySkipped)
  );
  const activationCompleted = Boolean(progress.activationCompleted && requiredReady);

  const completion = {
    mode: modeChosen,
    stripe: stripeLinked,
    stripeActioned,
    pmsDecision: pmsDecisionMade,
    pmsProvider: pmsProviderSelected,
    pmsConnected,
    pms: pmsDecisionMade && pmsProviderSelected && pmsConnected,
    firstProperty: firstPropertyCreated,
    propertyBasics: propertyBasicsConfigured,
    entryAccess: entryAccessConfigured,
    notificationPreference: notificationPreferenceConfigured,
    property: propertyOperationsConfigured,
    messaging: messagingViewed,
    activation: activationCompleted,
  };

  return {
    hostId: hostInfo?._id || hostDetails?._id || "",
    setupMode: progress.setupMode || "",
    selectedPms,
    pmsDecision,
    usesPms,
    supportedPms,
    requiresPmsConnection,
    stripeLinked,
    stripeSkipped,
    stripeActioned,
    activeProperties,
    firstActiveProperty,
    propertySkipped,
    completion,
    canActivate: requiredReady,
    activationCompleted,
    requiredReady,
    localProgress: progress,
  };
}

export function getNextIncompleteStep(state) {
  if (!state?.completion?.mode) return ONBOARDING_STEP_IDS.MODE;
  if (!state.completion.pmsDecision) return ONBOARDING_STEP_IDS.PMS;
  if (state.usesPms && !state.completion.pmsProvider) return ONBOARDING_STEP_IDS.PMS_PROVIDER;
  if (state.usesPms && !state.completion.pmsConnected) return ONBOARDING_STEP_IDS.PMS_PROVIDER;
  if (!state.propertySkipped && (!state.completion.firstProperty || !state.completion.propertyBasics)) {
    return ONBOARDING_STEP_IDS.PROPERTY_BASICS;
  }
  if (!state.propertySkipped && !state.completion.entryAccess) return ONBOARDING_STEP_IDS.PROPERTY_ACCESS;
  if (!state.propertySkipped && !state.completion.notificationPreference) return ONBOARDING_STEP_IDS.NOTIFICATIONS;
  if (state.completion.activation) return ONBOARDING_STEP_IDS.SUCCESS;
  if (!state.completion.messaging) return ONBOARDING_STEP_IDS.MESSAGING;
  return ONBOARDING_STEP_IDS.ACTIVATION;
}

export function getOnboardingMode(state) {
  if (state.activeProperties.length > 0) return "refresher";
  return "full";
}

export function getStepCompletion(state, stepId) {
  if (!state?.completion) return false;
  if (stepId === ONBOARDING_STEP_IDS.MODE) return state.completion.mode;
  if (stepId === ONBOARDING_STEP_IDS.STRIPE) return state.completion.stripe;
  if (stepId === ONBOARDING_STEP_IDS.PMS) return state.completion.pmsDecision;
  if (stepId === ONBOARDING_STEP_IDS.PMS_PROVIDER) {
    return Boolean(state.completion.pmsDecision && (!state.usesPms || state.completion.pmsConnected));
  }
  if (stepId === ONBOARDING_STEP_IDS.PROPERTY_BASICS) return state.propertySkipped || state.completion.propertyBasics;
  if (stepId === ONBOARDING_STEP_IDS.PROPERTY_ACCESS) return state.propertySkipped || state.completion.entryAccess;
  if (stepId === ONBOARDING_STEP_IDS.NOTIFICATIONS) return state.propertySkipped || state.completion.notificationPreference;
  if (stepId === ONBOARDING_STEP_IDS.MESSAGING) return state.completion.messaging;
  if (stepId === ONBOARDING_STEP_IDS.ACTIVATION) return state.completion.activation;
  return false;
}
